import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CalendarPlusIcon, CheckIcon, ClockIcon, CreditCardIcon, LoaderCircleIcon, LockIcon, MapPinIcon, PackageIcon, UsersIcon, XIcon } from 'lucide-react';
import { facilityConditions, facilitySlots, pickupWindows, savedCard, type Equipment, type Facility } from '../../data/loisirs';
export type BookingTarget = {
  kind: 'espace';
  facility: Facility;
} | {
  kind: 'equipement';
  equipment: Equipment;
};
interface BookingDialogProps {
  target: BookingTarget | null;
  onClose: () => void;
  onBooked: (id: string, summary: string) => void;
}
type Step = 'creneau' | 'details' | 'paiement' | 'traitement' | 'confirme';
const stepLabels: Record<Exclude<Step, 'traitement' | 'confirme'>, string> = {
  creneau: 'Créneau',
  details: 'Détails',
  paiement: 'Confirmation'
};
const primaryButton = 'inline-flex items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0';
const secondaryButton = 'inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const fieldClass = 'mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none';
export function BookingDialog({
  target,
  onClose,
  onBooked
}: BookingDialogProps) {
  const [step, setStep] = useState<Step>('creneau');
  const [slotId, setSlotId] = useState<string | null>(null);
  const [purpose, setPurpose] = useState('');
  const [people, setPeople] = useState('20');
  const [quantity, setQuantity] = useState('1');
  const [reference, setReference] = useState('');
  useEffect(() => {
    if (!target) {
      setStep('creneau');
      setSlotId(null);
      setPurpose('');
      setPeople('20');
      setQuantity('1');
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [target, onClose]);
  const isFacility = target?.kind === 'espace';
  const slots = useMemo(() => {
    if (!target) return [];
    return target.kind === 'espace' ? facilitySlots.filter(slot => slot.facilityId === target.facility.id) : pickupWindows.filter(window => window.equipmentId === target.equipment.id);
  }, [target]);
  const slot = slots.find(item => item.id === slotId) ?? null;
  const title = target ? target.kind === 'espace' ? target.facility.name : target.equipment.name : '';
  const amount = target ? target.kind === 'espace' ? slot && 'amount' in slot ? slot.amount : target.facility.price : target.equipment.price : '';
  const free = !isFacility && target?.kind === 'equipement' && target.equipment.price === 'Gratuit';
  const confirm = () => {
    if (!slot || !target) return;
    setStep('traitement');
    window.setTimeout(() => {
      const ref = `RES-2026-${Math.floor(1000 + Math.random() * 8999)}`;
      setReference(ref);
      setStep('confirme');
      onBooked(target.kind === 'espace' ? target.facility.id : target.equipment.id, `${slot.day} · ${slot.time}`);
    }, 1200);
  };
  const summary = slot && <dl className="rounded-2xl border border-slate-200 bg-ville-sand p-4 text-sm" data-id="element-1689" data-mp="2yzoo1jzeahh8">
      <div className="flex items-start justify-between gap-4" data-id="element-1690" data-mp="33zkt7mjtweyd">
        <div className="min-w-0" data-id="element-1691" data-mp="34146r5e3obwg">
          <dt className="sr-only" data-id="element-1692" data-mp="2paomptxf79zv">Réservation</dt>
          <dd className="font-extrabold text-ville-navy" data-id="element-1693" data-mp="2p9ve0newiaf3">{title}</dd>
          <dd className="mt-1 flex items-center gap-1.5 text-slate-600" data-id="element-1694" data-mp="2u86nuy574kxb">
            <ClockIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1695" data-mp="2g9qjhdut36ic" />
            {slot.day} · {slot.time}
          </dd>
          <dd className="mt-0.5 flex items-center gap-1.5 text-slate-600" data-id="element-1696" data-mp="2la4r4ushwefu">
            <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1697" data-mp="26cccs5yopw38" />
            {'place' in slot ? slot.place : target?.kind === 'espace' ? target.facility.place : ''}
          </dd>
        </div>
        <p className="shrink-0 text-lg font-extrabold text-ville-navy" data-id="element-1698" data-mp="4kfqq2ew53u7">{free ? 'Gratuit' : amount}</p>
      </div>
    </dl>;
  return <AnimatePresence data-id="element-1699" data-mp="9gmvvszgqbb3">
      {target && <motion.div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-1700" data-mp="1u235b45bp9a3">
          <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-ville-navy/45" data-id="element-1701" data-mp="1yzuw1uu8epcp" />

          <motion.div role="dialog" aria-modal="true" aria-label={`Réserver ${title}`} initial={{
        y: 24,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 16,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8" data-id="element-1702" data-mp="28q7r4gzoa0x3">
            <div className="flex items-start justify-between gap-4" data-id="element-1703" data-mp="2neuiw5t2u7d4">
              <div className="min-w-0" data-id="element-1704" data-mp="2ngp4zng345is">
                <p className="text-xs font-bold uppercase tracking-wider text-ville-indigo" data-id="element-1705" data-mp="2sdfgrykuweye">
                  {isFacility ? 'Réservation d’un espace' : 'Location d’équipement'}
                </p>
                <h2 className="mt-1 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-1706" data-mp="31f57t3attbvn">{title}</h2>
                <p className="mt-1 text-sm text-slate-600" data-id="element-1707" data-mp="36e8fywm3ry7u">
                  {isFacility && target.kind === 'espace' ? `${target.facility.capacity} · ${target.facility.place}` : target.kind === 'equipement' ? target.equipment.detail : ''}
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Fermer" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1708" data-mp="3k77vuzpsrufx">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-1709" data-mp="34tt4hcj3oew" />
              </button>
            </div>

            {step !== 'traitement' && step !== 'confirme' && <ol className="mt-5 flex items-center gap-2 text-xs font-bold" data-id="element-1710" data-mp="3q8rv27xarpif">
                {(Object.keys(stepLabels) as Array<keyof typeof stepLabels>).map((key, index) => {
            const order: Step[] = ['creneau', 'details', 'paiement'];
            const done = order.indexOf(step) > index;
            const active = step === key;
            return <li key={key} className="flex items-center gap-2" data-id="element-1711" data-mp="czvjfgijiwj2" data-mpi={index}>
                      <span className={`rounded-full px-3 py-1 ${active ? 'bg-ville-navy text-white' : done ? 'bg-ville-mist text-ville-deep' : 'bg-slate-100 text-slate-500'}`} data-id="element-1712" data-mp="19iks616ft4gf">
                        {stepLabels[key]}
                      </span>
                      {index < 2 && <span className="h-px w-4 bg-slate-200" aria-hidden="true" data-id="element-1713" data-mp="1eg4cfirpd9p1" />}
                    </li>;
          })}
              </ol>}

            <div className="mt-5" data-id="element-1714" data-mp="277e7zlqq8soi">
              {step === 'creneau' && <div data-id="element-1715" data-mp="2c4o2vgwvxwjq">
                  <p className="text-[15px] font-bold text-ville-navy" data-id="element-1716" data-mp="2c4xxjhsvkrvr">
                    {isFacility ? `${slots.length} créneaux libres` : `${slots.length} plages de cueillette`}
                  </p>
                  <p className="mt-1 text-sm text-slate-500" data-id="element-1717" data-mp="lss0s3n2ymai">
                    {isFacility ? 'Choisissez le bloc souhaité. Le tarif varie selon la durée et l’heure.' : 'Choisissez où et quand récupérer l’équipement.'}
                  </p>

                  <ul className="mt-4 space-y-3" data-id="element-1718" data-mp="301b0u1x2vupm">
                    {slots.map(item => {
                const active = slotId === item.id;
                return <li key={item.id} data-id="element-1719" data-mp="3dyhgvoqf3fb5">
                          <button type="button" aria-pressed={active} onClick={() => setSlotId(item.id)} className={`w-full rounded-2xl border p-4 text-left transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-navy bg-ville-sand' : 'border-slate-200 hover:border-ville-indigo'}`} data-id="element-1720" data-mp="3a09zcro4q9yw">
                            <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-1721" data-mp="1yjthkrp3rh8v">
                              <p className="text-sm font-extrabold text-ville-navy" data-id="element-1722" data-mp="jsce7gibeefo">
                                {item.day} · {item.time}
                              </p>
                              {'amount' in item ? <p className="text-sm font-bold text-ville-navy" data-id="element-1723" data-mp="5tcrieed49m5">{item.amount}</p> : <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-bold text-slate-600" data-id="element-1724" data-mp="ujeen2zmkmi">
                                  Cueillette
                                </span>}
                            </div>
                            <p className="mt-1 flex items-center gap-1.5 text-[13px] text-slate-600" data-id="element-1725" data-mp="3j8iu3brqqo4q">
                              <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1726" data-mp="3e8wcyr8sknqn" />
                              {'place' in item ? item.place : target.kind === 'espace' ? target.facility.place : ''}
                            </p>
                            {'left' in item && <p className="mt-1 text-[13px] text-slate-500" data-id="element-1727" data-mp="1yqq426xm2w6q">
                                {item.duration} · {item.left}
                              </p>}
                            {'note' in item && item.note && <p className="mt-1 text-[13px] font-semibold text-ville-deep" data-id="element-1728" data-mp="1azufrjsd6j48">{item.note}</p>}
                          </button>
                        </li>;
              })}
                  </ul>

                  <button type="button" disabled={!slot} onClick={() => setStep('details')} className={`mt-6 w-full ${primaryButton}`} data-id="element-1729" data-mp="1juse56ea6o2y">
                    Continuer
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1730" data-mp="1ulzgxs6zhqmg" />
                  </button>
                </div>}

              {step === 'details' && <div data-id="element-1731" data-mp="24cc13javzhs2">
                  {summary}

                  {isFacility ? <div className="mt-5 space-y-4" data-id="element-1732" data-mp="1ze0rn9oi79ad">
                      <div data-id="element-1733" data-mp="1zgdf22d16but">
                        <label htmlFor="booking-motif" className="block text-xs font-bold text-slate-500" data-id="element-1734" data-mp="1bog9gt06cg25">
                          Motif de la réservation
                        </label>
                        <input id="booking-motif" value={purpose} onChange={event => setPurpose(event.target.value)} placeholder="Ex. fête de famille, pratique d’équipe…" className={fieldClass} data-id="element-1735" data-mp="1kpe8sxzo3zee" />
                      </div>
                      <div data-id="element-1736" data-mp="2s50zjvpygx7b">
                        <label htmlFor="booking-personnes" className="block text-xs font-bold text-slate-500" data-id="element-1737" data-mp="2zmqrhv12ket0">
                          Nombre de personnes
                        </label>
                        <input id="booking-personnes" type="number" min={1} value={people} onChange={event => setPeople(event.target.value)} className={fieldClass} data-id="element-1738" data-mp="2poazzqy4r3iz" />
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500" data-id="element-1739" data-mp="3naqh2rmn9z60">
                          <UsersIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1740" data-mp="38mdtahxpnmml" />
                          Capacité de l’espace : {target.kind === 'espace' ? target.facility.capacity : ''}
                        </p>
                      </div>
                    </div> : <div className="mt-5 space-y-4" data-id="element-1741" data-mp="9xx2op5mar8x">
                      <div data-id="element-1742" data-mp="2idy5smw2l0oe">
                        <label htmlFor="booking-quantite" className="block text-xs font-bold text-slate-500" data-id="element-1743" data-mp="2if97mh5yfycv">
                          Quantité
                        </label>
                        <input id="booking-quantite" type="number" min={1} value={quantity} onChange={event => setQuantity(event.target.value)} className={fieldClass} data-id="element-1744" data-mp="28ficqedhfhc9" />
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500" data-id="element-1745" data-mp="2w6ebxwk9petj">
                          <PackageIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1746" data-mp="2rdbdba0mvf6h" />
                          {target.kind === 'equipement' ? target.equipment.detail : ''}
                        </p>
                      </div>
                      <div data-id="element-1747" data-mp="3f4gx209p2rm2">
                        <label htmlFor="booking-usage" className="block text-xs font-bold text-slate-500" data-id="element-1748" data-mp="3f48g9xagb25l">
                          Usage prévu (optionnel)
                        </label>
                        <input id="booking-usage" value={purpose} onChange={event => setPurpose(event.target.value)} placeholder="Ex. sortie familiale au parc…" className={fieldClass} data-id="element-1749" data-mp="3re6jvdm0ubxh" />
                      </div>
                    </div>}

                  <div className="mt-6 flex items-center justify-between gap-4" data-id="element-1750" data-mp="e7emny2si0cn">
                    <button type="button" onClick={() => setStep('creneau')} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1751" data-mp="iyeg2tcpa0ae">
                      Retour
                    </button>
                    <button type="button" onClick={() => setStep('paiement')} className={primaryButton} data-id="element-1752" data-mp="878fy5vnnh1n">
                      Continuer
                      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1753" data-mp="3ars7csdb4p3" />
                    </button>
                  </div>
                </div>}

              {step === 'paiement' && <div data-id="element-1754" data-mp="d51k6tdee0za">
                  {summary}

                  <dl className="mt-4 space-y-2 text-sm" data-id="element-1755" data-mp="qxq6q4wxg2kd">
                    {isFacility ? <>
                        <div className="flex justify-between gap-4" data-id="element-1756" data-mp="lyn22lkuzycx">
                          <dt className="text-slate-500" data-id="element-1757" data-mp="10x8k22ic0q3g">Motif</dt>
                          <dd className="font-semibold text-ville-navy" data-id="element-1758" data-mp="10ucoulxvcfip">{purpose || 'Non précisé'}</dd>
                        </div>
                        <div className="flex justify-between gap-4" data-id="element-1759" data-mp="32nab1yx549uo">
                          <dt className="text-slate-500" data-id="element-1760" data-mp="2xoz1lparc1cz">Personnes</dt>
                          <dd className="font-semibold text-ville-navy" data-id="element-1761" data-mp="2xqto381og1j6">{people}</dd>
                        </div>
                      </> : <div className="flex justify-between gap-4" data-id="element-1762" data-mp="1i6az6g4au9ou">
                        <dt className="text-slate-500" data-id="element-1763" data-mp="13h4ysotq6dfd">Quantité</dt>
                        <dd className="font-semibold text-ville-navy" data-id="element-1764" data-mp="13gv9z4kf2cb9">{quantity}</dd>
                      </div>}
                    <div className="flex justify-between gap-4 border-t border-slate-200 pt-2" data-id="element-1765" data-mp="ugoy1n4d9ke7">
                      <dt className="font-bold text-ville-navy" data-id="element-1766" data-mp="yi21xrnwfdih">Total</dt>
                      <dd className="font-extrabold text-ville-navy" data-id="element-1767" data-mp="yfxpumfuj874">{free ? 'Gratuit' : amount}</dd>
                    </div>
                  </dl>

                  {!free && <div className="mt-4 flex items-center gap-3 rounded-2xl border border-ville-indigo bg-ville-indigo-soft p-4" data-id="element-1768" data-mp="phvtik7253q6">
                      <CreditCardIcon className="h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-1769" data-mp="2euh47454v43n" />
                      <div className="min-w-0" data-id="element-1770" data-mp="16oio5dyt5kfy">
                        <p className="text-sm font-bold text-ville-navy" data-id="element-1771" data-mp="16n8q2orygc9b">
                          {savedCard.brand} •••• {savedCard.last4}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-1772" data-mp="xvoto9asiehi">Carte enregistrée · expire {savedCard.expiry}</p>
                      </div>
                    </div>}

                  <ul className="mt-4 space-y-2" data-id="element-1773" data-mp="ny0wlcppouw6">
                    {(isFacility ? facilityConditions : ['Présentez votre carte numérique au moment de la cueillette.', 'L’équipement doit être rapporté au même point de service.', 'Annulation sans frais jusqu’à 24 h avant la cueillette.']).map(condition => <li key={condition} className="flex items-start gap-2 text-[13px] text-slate-600" data-id="element-1774" data-mp="29912kylepnaj">
                        <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ville-deep" aria-hidden="true" data-id="element-1775" data-mp="2bvteh70tpv9u" />
                        {condition}
                      </li>)}
                  </ul>

                  {!free && <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500" data-id="element-1776" data-mp="2lmxqpyz4720z">
                      <LockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1777" data-mp="2qjo7ypmno3nz" />
                      Paiement traité par la passerelle sécurisée de la Ville de Brossard.
                    </p>}

                  <div className="mt-6 flex items-center justify-between gap-4" data-id="element-1778" data-mp="1xwk78wulibi0">
                    <button type="button" onClick={() => setStep('details')} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1779" data-mp="22uc0pvaw25ob">
                      Retour
                    </button>
                    <button type="button" onClick={confirm} className={primaryButton} data-id="element-1780" data-mp="3j8t2mmn7iwhy">
                      {free ? 'Confirmer la réservation' : `Payer ${amount}`}
                    </button>
                  </div>
                </div>}

              {step === 'traitement' && <div className="py-10 text-center" data-id="element-1781" data-mp="ua8ir80imqg">
                  <LoaderCircleIcon className="mx-auto h-8 w-8 animate-spin text-ville-indigo" aria-hidden="true" data-id="element-1782" data-mp="30b01npw58wn7" />
                  <p className="mt-4 text-sm font-semibold text-ville-navy" role="status" data-id="element-1783" data-mp="34eel6qjajv5e">
                    {free ? 'Confirmation de la réservation…' : 'Traitement du paiement…'}
                  </p>
                </div>}

              {step === 'confirme' && slot && <div data-id="element-1784" data-mp="okxo7yo66gnk">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-1785" data-mp="tkadsr6fe1tu">
                    <CheckIcon className="h-6 w-6" data-id="element-1786" data-mp="2e45qc62i14rt" />
                  </span>
                  <h3 className="mt-3 text-lg font-extrabold text-ville-navy" data-id="element-1787" data-mp="29404x7mtoei6">Réservation confirmée</h3>
                  <p className="mt-1 text-sm text-slate-600" data-id="element-1788" data-mp="24af5db5kzcgv">
                    {title} · {slot.day}, {slot.time}. Référence {reference}.{' '}
                    {isFacility ? 'Votre carte numérique déverrouille l’accès au local.' : 'Présentez votre carte numérique au point de cueillette.'}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3" data-id="element-1789" data-mp="1lep2llptqc1x">
                    <Link to="/ma-carte" className={secondaryButton} data-id="element-1790" data-mp="1geklpul07fb6">
                      Voir ma carte numérique
                    </Link>
                    <button type="button" className={secondaryButton} data-id="element-1791" data-mp="3fqdk9vnfwsq0">
                      <CalendarPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1792" data-mp="3as2blo8vso9d" />
                      Ajouter au calendrier
                    </button>
                  </div>
                  <button type="button" onClick={onClose} className={`mt-4 w-full ${primaryButton}`} data-id="element-1793" data-mp="1du4onqg1t1kp">
                    Terminer
                  </button>
                </div>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}